/** French formatting and labels. Timestamps are shown in the browser's time zone. */
import type {
  AccentColor,
  AgentGender,
  ApprovalStatus,
  AgentSettings,
  AvatarStyle,
  Autonomy,
  Channel,
  ChatKind,
  Effort,
  EmojiLevel,
  Formality,
  MachineSize,
  MachineView,
  MemoryItemView,
  MemoryKind,
  MessageLength,
  MissionStatus,
  Persona,
  ProfileBlockView,
  RunStatus,
  TurnTrigger,
  TurnView,
} from "../../shared/contracts";
import { MACHINE_SIZES } from "../../shared/contracts";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const timeFmt = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const shortDateFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });
const shortDateYearFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const numberFmt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const smallNumberFmt = new Intl.NumberFormat("fr-FR", { maximumSignificantDigits: 2 });
const intFmt = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** "08:12" */
export function fmtTime(ms: number): string {
  return timeFmt.format(ms);
}

/** "mercredi 29 octobre" */
export function fmtDay(ms: number): string {
  return dayFmt.format(ms);
}

/** "7 h 30", "18 h": a time as a French sentence writes it (and as the agent's messages say it). */
export function fmtSpokenTime(ms: number): string {
  const d = new Date(ms);
  return spokenHm(`${d.getHours()}:${d.getMinutes()}`);
}

/** "07:30" → "7 h 30", "18:00" → "18 h" (unchanged when not HH:MM). */
export function spokenHm(hm: string): string {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(hm.trim());
  if (!m) return hm;
  const minutes = Number(m[2]);
  return `${Number(m[1])}\u00a0h${minutes ? `\u00a0${String(minutes).padStart(2, "0")}` : ""}`;
}

/** "aujourd'hui à 08:12", "hier à 08:12", "demain à 08:12", "28 oct. à 08:12" ("à 8 h 12" with `spoken`, in sentences) */
export function fmtDateTime(ms: number, now: number = Date.now(), opts: { spoken?: boolean } = {}): string {
  const diffDays = Math.round((startOfDay(ms) - startOfDay(now)) / DAY);
  const time = opts.spoken ? fmtSpokenTime(ms) : fmtTime(ms);
  if (diffDays === 0) return `aujourd'hui à ${time}`;
  if (diffDays === -1) return `hier à ${time}`;
  if (diffDays === 1) return `demain à ${time}`;
  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear();
  return `${(sameYear ? shortDateFmt : shortDateYearFmt).format(ms)} à ${time}`;
}

/** "à l'instant", "il y a 5 min", "dans 2 h", else an absolute date. */
export function fmtRelative(ms: number, now: number = Date.now()): string {
  const diff = ms - now;
  const abs = Math.abs(diff);
  if (abs < MINUTE) return diff <= 0 ? "à l'instant" : "dans un instant";
  if (abs < HOUR) {
    const m = Math.round(abs / MINUTE);
    return diff < 0 ? `il y a ${m} min` : `dans ${m} min`;
  }
  if (abs < 6 * HOUR) {
    const h = Math.round(abs / HOUR);
    return diff < 0 ? `il y a ${h} h` : `dans ${h} h`;
  }
  return fmtDateTime(ms, now);
}

/** "45 s", "3 min", "1 h 05" */
export function fmtDuration(ms: number): string {
  if (ms < MINUTE) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (ms < HOUR) return `${Math.round(ms / MINUTE)} min`;
  const h = Math.floor(ms / HOUR);
  const m = Math.round((ms % HOUR) / MINUTE);
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

/** "3,42 $", "0,0042 $" */
export function fmtUsd(usd: number): string {
  if (!Number.isFinite(usd)) return "—";
  if (usd !== 0 && Math.abs(usd) < 0.01) return `${smallNumberFmt.format(usd)} $`;
  return `${numberFmt.format(usd)} $`;
}

export function fmtNumber(n: number): string {
  return intFmt.format(n);
}

/** "812 o", "182 Ko", "1,4 Mo", "18 Mo": a file's size as the agent words it (channels/outbound-files.ts formatSize). */
export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  const k = Math.round(n / 1024);
  if (k < 1024) return `${k} Ko`;
  const m = n / (1024 * 1024);
  return `${(m >= 10 ? Math.round(m).toString() : m.toFixed(1).replace(/\.0$/, "")).replace(".", ",")} Mo`;
}

/** Greeting for the local hour. */
export function greeting(now: number = Date.now()): string {
  const h = new Date(now).getHours();
  if (h < 5) return "Bonne nuit";
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

export function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** Keeps only https URLs (brief cards and links coming from the agent). */
export function safeHttpsUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

/** Same-origin in-app path ("/runs/…"), or null. Protects redirects taken from URLs and messages. */
export function safeInternalPath(path: unknown): string | null {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return null;
  return path;
}

// ─────────────── Labels (the E2E tests select on several of these: keep them exact) ───────────────

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  queued: "En préparation",
  running: "En cours",
  waiting_approval: "En attente de validation",
  waiting_user: "Question pour toi",
  done: "Terminée",
  failed: "Échouée",
  cancelled: "Annulée",
};

export function runStatusLabel(status: string): string {
  return RUN_STATUS_LABEL[status as RunStatus] ?? status;
}

export function isRunActive(status: string): boolean {
  return status === "queued" || status === "running" || status === "waiting_approval" || status === "waiting_user";
}

export const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: "En attente",
  approved: "Validée",
  rejected: "Refusée",
  executed: "Exécutée",
  failed: "Échec",
  expired: "Expirée",
};

export function approvalStatusLabel(status: string): string {
  return APPROVAL_STATUS_LABEL[status as ApprovalStatus] ?? status;
}

export const MACHINE_STATE_LABEL: Record<MachineView["state"], string> = {
  running: "Allumé",
  sleeping: "En veille",
  stopped: "Éteint",
  starting: "Réveil…",
  stopping: "Arrêt…",
  unavailable: "Indisponible",
  online: "En ligne",
  offline: "Hors ligne",
};

export function machineStateLabel(state: string): string {
  return MACHINE_STATE_LABEL[state as MachineView["state"]] ?? state;
}

export const MISSION_STATUS_LABEL: Record<MissionStatus, string> = {
  active: "Active",
  paused: "En pause",
  done: "Accomplie",
  cancelled: "Abandonnée",
};

// Partial on purpose: new triggers added to the contracts fall back to their id instead of breaking the build.
export const TRIGGER_LABEL: Partial<Record<TurnTrigger, string>> = {
  user_message: "Message",
  onboarding: "Premier contact",
  morning_brief: "Brief du matin",
  evening_recap: "Récap du soir",
  checkin: "Check-in",
  wakeup: "Rappel",
  follow_up: "Relance",
  mission_result: "Résultat de mission",
  approval_decision: "Après une validation",
  question_answer: "Réponse à une question",
  device_event: "Événement d'une machine",
  sweep: "Veille des apps",
  meeting_prep: "Préparation de rendez-vous",
  digest: "Résumé",
  re_engagement: "Prise de nouvelles",
  compaction: "Mise en ordre de la mémoire",
};

export function triggerLabel(trigger: string): string {
  return TRIGGER_LABEL[trigger as TurnTrigger] ?? trigger;
}

export const TURN_STATUS_LABEL: Record<TurnView["status"], string> = {
  queued: "En file",
  running: "En cours",
  done: "Terminé",
  failed: "Échec",
  cancelled: "Annulé",
};

export const CHANNEL_LABEL: Record<Channel, string> = {
  web: "Web",
  imessage: "iMessage",
  sms: "SMS",
  telegram: "Telegram",
  email: "Email",
};

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel as Channel] ?? channel;
}

export const CHAT_KIND_LABEL: Record<ChatKind, string> = {
  chat: "",
  proactive: "De sa propre initiative",
  brief: "Brief",
  approval: "Validation",
  mission: "Mission",
  system: "Système",
};

export const RISK_LABEL: Record<"write" | "external" | "critical", string> = {
  write: "Modification",
  external: "Envoi vers l'extérieur",
  critical: "Sensible",
};

export const MEMORY_KIND_LABEL: Record<MemoryKind, string> = {
  fact: "Fait",
  preference: "Préférence",
  relationship: "Relation",
  event: "Événement",
  instruction: "Consigne",
  plan: "Projet",
  task: "Tâche",
  agent_action: "Action de l'agent",
  agent_opinion: "Avis de l'agent",
  insight: "Idée",
};

export const MEMORY_SOURCE_LABEL: Record<MemoryItemView["source"], string> = {
  extraction: "Appris en discutant",
  user: "Ajouté par toi",
  agent_tool: "Noté par l'agent",
  consolidation: "Synthèse",
  import: "Importé",
};

export const PROFILE_LABEL: Record<ProfileBlockView["label"], string> = {
  identite: "Identité",
  preferences: "Préférences",
  proches: "Proches",
  projets: "Projets",
  routines: "Routines",
  consignes: "Consignes",
};

export const PROFILE_ORDER: ProfileBlockView["label"][] = ["identite", "preferences", "proches", "projets", "routines", "consignes"];

export const PERSONAS: { value: Persona; label: string; description: string; emoji: string }[] = [
  { value: "chaleureux", label: "Chaleureux", description: "Attentionné, encourage, va à l'essentiel.", emoji: "🤗" },
  { value: "pro", label: "Posé et pro", description: "Précis, sobre, sans fioritures.", emoji: "🧑‍💼" },
  { value: "direct", label: "Direct", description: "Franc et bref, sans détour.", emoji: "🎯" },
  { value: "drole", label: "Drôle", description: "De l'humour, mais toujours fiable.", emoji: "😄" },
  { value: "zen", label: "Zen", description: "Calme et apaisant, jamais pressé.", emoji: "🌿" },
  { value: "coach", label: "Coach", description: "Motivant, t'aide à avancer sur tes objectifs.", emoji: "💪" },
];

export const EMOJI_LEVEL_OPTIONS: { value: EmojiLevel; label: string }[] = [
  { value: "auto", label: "Au naturel" },
  { value: "none", label: "Aucun" },
  { value: "few", label: "Un peu" },
  { value: "many", label: "Beaucoup" },
];

export const MESSAGE_LENGTH_OPTIONS: { value: MessageLength; label: string }[] = [
  { value: "auto", label: "Au naturel" },
  { value: "short", label: "Court" },
  { value: "detailed", label: "Détaillé" },
];

export const FORMALITY_OPTIONS: { value: Formality; label: string }[] = [
  { value: "tu", label: "Tutoiement" },
  { value: "vous", label: "Vouvoiement" },
];

export const GENDER_OPTIONS: { value: AgentGender; label: string; example: string }[] = [
  { value: "masculin", label: "Au masculin", example: "« ravi », « prêt »" },
  { value: "feminin", label: "Au féminin", example: "« ravie », « prête »" },
  { value: "neutre", label: "Neutre", example: "tournures sans accord" },
];

/** The agent colors: `swatch` is the light-theme accent (styles.css holds both themes' values). */
export const ACCENT_OPTIONS: { value: AccentColor; label: string; swatch: string }[] = [
  // "violet" is the stored default value: it now paints the brand's terre cuite.
  { value: "violet", label: "Terre cuite", swatch: "#a9552f" },
  { value: "bleu", label: "Bleu", swatch: "#3e6a8a" },
  { value: "menthe", label: "Menthe", swatch: "#3f7363" },
  { value: "ambre", label: "Ambre", swatch: "#9a6418" },
  { value: "rose", label: "Rose", swatch: "#a14868" },
  { value: "graphite", label: "Graphite", swatch: "#4a4640" },
];

export const AVATAR_STYLE_OPTIONS: { value: AvatarStyle; label: string; description: string; emoji: string }[] = [
  { value: "3d", label: "Personnage 3D stylisé", description: "Rond, doux, façon film d'animation.", emoji: "🧸" },
  { value: "anime", label: "Anime doux", description: "Traits fins, grands yeux expressifs.", emoji: "🌸" },
  { value: "flat", label: "Illustration flat", description: "Formes simples, couleurs franches.", emoji: "🎨" },
  { value: "pixel", label: "Pixel art", description: "Rétro, comme un héros de jeu vidéo.", emoji: "👾" },
];

export const AUTONOMY_OPTIONS: { value: Autonomy; label: string; description: string }[] = [
  { value: "prudent", label: "Prudent", description: "Te demande avant toute modification de tes apps ou tout envoi." },
  { value: "equilibre", label: "Équilibré", description: "Agit seul ; te demande seulement avant d'écrire à quelqu'un, d'inviter, de payer, de supprimer, de publier ou d'ouvrir un contenu confidentiel." },
  { value: "autonome", label: "Autonome", description: "Comme Équilibré, mais ouvre aussi les contenus confidentiels sans te demander." },
];

export const EFFORT_OPTIONS: { value: Effort; label: string }[] = [
  { value: "low", label: "Rapide" },
  { value: "medium", label: "Normal" },
  { value: "high", label: "Approfondi" },
];

export const PRIMARY_CHANNEL_OPTIONS: { value: AgentSettings["primaryChannel"]; label: string }[] = [
  { value: "web", label: "L'application (et ses notifications)" },
  { value: "imessage", label: "iMessage / SMS" },
  { value: "telegram", label: "Telegram" },
  { value: "email", label: "Email" },
];

/** Model-facing tool names → short French chip labels. */
const TOOL_LABEL: Record<string, string> = {
  apps_connect: "Connexion d'une app",
  apps_execute: "Action dans une app",
  apps_search: "Recherche d'outils",
  brief_publish: "Brief",
  computer_task: "Tâche sur ordinateur",
  computer_lanes: "Tâches en parallèle",
  run_code: "Script",
  desktop_ui: "Applications de l'ordinateur",
  parallel_tasks: "Sous-agents en parallèle",
  memory_forget: "Oubli",
  memory_save: "Souvenir noté",
  memory_search: "Souvenirs",
  mission_create: "Nouvelle mission",
  mission_list: "Liste des missions",
  mission_update: "Mise à jour de mission",
  schedule_wakeup: "Rappel programmé",
  send_message: "Message",
  send_file: "Envoi de fichier",
  show_ui: "Interface",
  wakeup_cancel: "Rappel annulé",
  web_search: "Recherche web",
  web_fetch: "Lecture d'une page",
  bash: "Terminal",
  str_replace_based_edit_tool: "Fichiers",
  computer: "Écran",
  // GPT computer use (screen missions): opening a page in the computer's browser.
  open_url: "Navigateur",
  ask_user: "Question",
  mission_finish: "Fin de mission",
  mission_answer: "Réponse à une mission",
  memory_why: "Souvenirs",
  digest_add: "Note pour le brief",
  proactivity_update: "Réglages proactifs",
  stay_silent: "Silence",
  // Steps of runs of the former in-computer executor (their history stays readable) and relayed approvals.
  terminal: "Terminal",
  process: "Terminal",
  read_file: "Fichier",
  write_file: "Fichier",
  patch: "Fichier",
  search_files: "Fichier",
  execute_code: "Code",
  vision_analyze: "Image",
  memory: "Mémoire",
  skill_view: "Compétence",
  skill_manage: "Compétence",
  skills_list: "Compétence",
  todo: "Tâches",
  session_search: "Historique",
  clarify: "Question",
  validation: "Validation",
};

/** Families of tool ids (browser_*, skill_*, … of older runs) → label; anything else unknown is shown as "Action". */
const TOOL_FAMILY: [RegExp, string][] = [
  [/^(screenshot|zoom|left_click|right_click|double_click|triple_click|middle_click|mouse_move|left_click_drag|scroll|type|key|hold_key|wait|cursor_position|left_mouse_down|left_mouse_up)$/, "Écran"],
  [/^browser(_|$)/, "Navigateur"],
  [/^skills?(_|$)/, "Compétence"],
  [/^memor(y|ies)(_|$)/, "Mémoire"],
  [/(^|_)(file|files|dir|directory)(_|$)/, "Fichier"],
  [/^(terminal|shell|bash|process)(_|$)/, "Terminal"],
  [/^(web|search)(_|$)/, "Recherche web"],
];

export function toolLabel(name: string): string {
  if (TOOL_LABEL[name]) return TOOL_LABEL[name]!;
  for (const [re, label] of TOOL_FAMILY) if (re.test(name)) return label;
  return "Action";
}

/** Recurrence → French ("Chaque jour", "Toutes les 30 min", "Une fois"). */
export function cadenceLabel(minutes: number | null | undefined): string {
  if (!minutes) return "Une fois";
  if (minutes === 60) return "Toutes les heures";
  if (minutes === 1440) return "Chaque jour";
  if (minutes === 10080) return "Chaque semaine";
  if (minutes < 60) return `Toutes les ${minutes} min`;
  if (minutes % 1440 === 0) return `Tous les ${minutes / 1440} jours`;
  if (minutes % 60 === 0) return `Toutes les ${minutes / 60} h`;
  return `Toutes les ${minutes} min`;
}

export const CADENCE_OPTIONS: { value: string; label: string; minutes: number | null }[] = [
  { value: "once", label: "Une fois", minutes: null },
  { value: "hourly", label: "Toutes les heures", minutes: 60 },
  { value: "daily", label: "Chaque jour", minutes: 1440 },
  { value: "weekly", label: "Chaque semaine", minutes: 10080 },
];

export function cadenceValue(minutes: number | null | undefined): string {
  return CADENCE_OPTIONS.find((o) => o.minutes === (minutes ?? null))?.value ?? "custom";
}

const decimal = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

/** "0,5 vCPU · 4 Go de RAM · 8 Go de disque" */
export function sizeSpecs(size: MachineSize | undefined): string {
  if (!size) return "";
  if (typeof size === "string") {
    const s = MACHINE_SIZES[size];
    if (!s) return size;
    return `${decimal.format(s.vcpu)} vCPU · ${decimal.format(s.memoryGiB)} Go de RAM · ${decimal.format(s.diskGB)} Go de disque`;
  }
  return `${decimal.format(size.vcpu)} vCPU · ${decimal.format(size.memoryMib / 1024)} Go de RAM · ${decimal.format(size.diskMb / 1000)} Go de disque`;
}

export function sizeLabel(size: MachineSize | undefined): string {
  if (!size) return "";
  if (typeof size === "string") return MACHINE_SIZES[size]?.label ?? size;
  return "Sur mesure";
}

/** Machine target ("none" | "cloud" | "local:<id>") → French. */
export function machineTargetLabel(target: string, machines?: MachineView[]): string {
  if (target === "none") return "Sans ordinateur";
  if (target === "cloud") return "Ordinateur cloud";
  if (target.startsWith("local:")) {
    const id = target.slice(6);
    const m = machines?.find((x) => x.id === id);
    return m ? `Ordinateur local « ${m.name} »` : "Ordinateur local";
  }
  return target;
}

/** Masked display of an E.164 number: "+33 6 •• •• •• 78". */
export function prettyPhone(phone: string): string {
  const m = /^\+33(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(phone);
  if (m) return `+33 ${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]}`;
  return phone;
}

// ─────────────── Missions in plain words ───────────────

/** "aujourd'hui", "hier", "lundi" (this week), else "28 oct.". */
export function fmtDayWord(ms: number, now: number = Date.now()): string {
  const diffDays = Math.round((startOfDay(ms) - startOfDay(now)) / DAY);
  if (diffDays === 0) return "aujourd'hui";
  if (diffDays === -1) return "hier";
  if (diffDays === 1) return "demain";
  if (Math.abs(diffDays) < 7) return new Intl.DateTimeFormat("fr-FR", { weekday: "long" }).format(ms);
  return shortDateFmt.format(ms);
}

/** One plain line for a run: "Nova y travaille", "Une question pour toi", "Terminée hier". */
export function runPlainLine(run: { status: string; finishedAt: number | null; createdAt: number }, agentName: string, now: number = Date.now()): string {
  switch (run.status) {
    case "queued":
      return "Ça démarre";
    case "running":
      return `${agentName} y travaille`;
    case "waiting_user":
      return "Une question pour toi";
    case "waiting_approval":
      return "Attend ton accord";
    case "done":
      return `Terminée ${fmtDayWord(run.finishedAt ?? run.createdAt, now)}`;
    case "failed":
      return "N'a pas abouti";
    case "cancelled":
      return "Annulée";
    default:
      return runStatusLabel(run.status);
  }
}

/** When a recurring mission comes back, in words: "Chaque lundi à 8 h", "Chaque jour à 7 h 30", "Toutes les 3 h". */
export function scheduleWords(cadenceMinutes: number | null, nextRunAt: number | null, now: number = Date.now()): string | null {
  const at = nextRunAt ? fmtSpokenTime(nextRunAt) : null;
  if (!cadenceMinutes) return nextRunAt ? `Prévue ${fmtDayWord(nextRunAt, now)} à ${at}` : null;
  if (cadenceMinutes === 10080 && nextRunAt) return `Chaque ${new Intl.DateTimeFormat("fr-FR", { weekday: "long" }).format(nextRunAt)} à ${at}`;
  if (cadenceMinutes === 1440 && nextRunAt) return `Chaque jour à ${at}`;
  return cadenceLabel(cadenceMinutes);
}

/** « il » / « elle » for the agent, after its grammatical gender; its name when neutral. */
export function agentPronoun(settings: { agentGender?: string; agentName: string } | undefined, capital = false): string {
  const g = settings?.agentGender;
  const word = g === "feminin" || g === "féminin" ? "elle" : g === "neutre" ? (settings?.agentName ?? "il") : "il";
  return capital && word === word.toLowerCase() ? capitalize(word) : word;
}

/** « seul » / « seule » after the agent's gender. */
export function agentAlone(settings: { agentGender?: string } | undefined): string {
  const g = settings?.agentGender;
  return g === "feminin" || g === "féminin" ? "seule" : "seul";
}
